/**
 * /unlocks — pay-to-unlock links (sell a file/link/secret for crypto).
 *
 * Each unlock is a shareable paywall URL. A buyer pays crypto straight to your
 * settlement wallet (non-custodial) and the secret is revealed on their paid
 * confirmation. The secret payload is write-only: it is never returned to this
 * dashboard after creation.
 */

import { useEffect, useState } from "react";
import {
  ApiError,
  createUnlock,
  deleteUnlock,
  listUnlocks,
  type UnlockPage,
} from "../lib/api";
import { Banner, Btn } from "../components/ui";
import { Ic } from "../components/Icons";

const usd = (cents: number): string =>
  "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function Unlocks() {
  const [items, setItems] = useState<UnlockPage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const refetch = async () => {
    try {
      const r = await listUnlocks();
      setItems(r.data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load unlocks.");
    }
  };
  useEffect(() => {
    void refetch();
  }, []);

  return (
    <div className="scroll">
      <div className="pad fade-in" style={{ maxWidth: 760 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
              Pay-to-unlock
            </h2>
            <p style={{ fontSize: 13.5, color: "var(--ink-3)", margin: "4px 0 0" }}>
              Sell a file, link, or secret for crypto. Buyers pay straight to your
              wallet and the content unlocks, non-custodial.
            </p>
          </div>
          {!creating && (
            <Btn onClick={() => setCreating(true)}>
              <Ic name="plus" size={14} /> New unlock
            </Btn>
          )}
        </div>

        {error && <Banner tone="fail">{error}</Banner>}

        {creating && (
          <NewUnlock
            onClose={() => setCreating(false)}
            onCreated={() => {
              setCreating(false);
              void refetch();
            }}
          />
        )}

        {items === null ? (
          <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>
            Loading…
          </div>
        ) : items.length === 0 && !creating ? (
          <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>
            <div style={{ fontSize: 14, marginBottom: 4 }}>No unlocks yet.</div>
            <div style={{ fontSize: 12.5 }}>
              Create one to sell a download link, a license key, or a private note.
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((u) => (
              <UnlockRow key={u.id} item={u} onChange={refetch} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UnlockRow({ item, onChange }: { item: UnlockPage; onChange: () => Promise<void> | void }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* ignore */
    }
  };
  const remove = async () => {
    if (!confirm("Disable this unlock link? Buyers will no longer be able to open it.")) return;
    setBusy(true);
    try {
      await deleteUnlock(item.id);
      await onChange();
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{item.title || "Untitled unlock"}</div>
          {item.description && (
            <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>{item.description}</div>
          )}
        </div>
        <div style={{ fontWeight: 700, fontSize: 16, whiteSpace: "nowrap" }}>{usd(item.price_usd_cents)}</div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
        <input
          readOnly
          value={item.url}
          onFocus={(e) => e.currentTarget.select()}
          style={{
            flex: 1,
            minWidth: 180,
            fontSize: 12.5,
            fontFamily: "var(--mono, monospace)",
            padding: "9px 11px",
            border: "1px solid var(--line, #e6e8eb)",
            borderRadius: 9,
            background: "var(--surface-2, #fafbfc)",
            color: "var(--ink-2, #374151)",
          }}
        />
        <Btn variant="ghost" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </Btn>
        <a href={item.url} target="_blank" rel="noopener noreferrer">
          <Btn variant="ghost">Open</Btn>
        </a>
        <Btn variant="ghost" onClick={remove} disabled={busy}>
          Disable
        </Btn>
      </div>
    </div>
  );
}

function NewUnlock({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [payload, setPayload] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [aup, setAup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "11px 13px",
    fontSize: 14,
    border: "1px solid var(--line, #e6e8eb)",
    borderRadius: 10,
    background: "var(--surface, #fff)",
    color: "var(--ink, #111827)",
    marginTop: 6,
    boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = { display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)", marginTop: 14 };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const p = parseFloat(price);
    if (!(p >= 1)) return setErr("Set a price of at least $1.");
    if (!payload.trim() && !file) return setErr("Attach a file or add the secret content to reveal.");
    if (file && file.size > 10 * 1024 * 1024) return setErr("The file must be 10 MB or smaller.");
    if (!aup) return setErr("Please accept the acceptable-use policy.");
    setBusy(true);
    try {
      await createUnlock({
        price_usd_cents: Math.round(p * 100),
        payload: payload.trim(),
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        aup_accepted: true,
        file,
      });
      onCreated();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Could not create the unlock.");
      setBusy(false);
    }
  };

  return (
    <form className="card" style={{ padding: 20, marginBottom: 14 }} onSubmit={submit}>
      <div style={{ fontWeight: 600, fontSize: 16 }}>New pay-to-unlock link</div>
      <label style={labelStyle}>Title (what they're buying)</label>
      <input style={inputStyle} maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="My premium file" />
      <label style={labelStyle}>Price (USD)</label>
      <input style={inputStyle} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="5.00" />
      <label style={labelStyle}>Description (optional)</label>
      <input style={inputStyle} maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="A short pitch shown on the paywall" />
      <label style={labelStyle}>File to deliver (optional, up to 10 MB): PDF, Word, zip, images, audio…</label>
      <input
        style={inputStyle}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.json,.zip,.epub,.png,.jpg,.jpeg,.webp,.gif,.mp3,.wav,.mp4,.mov"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />
      <label style={labelStyle}>Secret text to reveal after payment (optional if you attach a file)</label>
      <textarea
        style={{ ...inputStyle, minHeight: 80, resize: "vertical", fontFamily: "var(--mono, monospace)", fontSize: 13 }}
        maxLength={100000}
        value={payload}
        onChange={(e) => setPayload(e.target.value)}
        placeholder="https://drive.example.com/your-file  — or a license key, password, message…"
      />
      <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 16, fontSize: 12.5, color: "var(--ink-2)", cursor: "pointer" }}>
        <input type="checkbox" checked={aup} onChange={(e) => setAup(e.target.checked)} style={{ marginTop: 2 }} />
        <span>
          I confirm this is not adult, illegal, or non-consensual content, and I accept the
          acceptable-use policy. Crypto payments are final.
        </span>
      </label>
      {err && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="fail">{err}</Banner>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Btn type="submit" disabled={busy}>
          {busy ? "Creating…" : "Create unlock link"}
        </Btn>
        <Btn type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Btn>
      </div>
    </form>
  );
}
